import React, { useState } from 'react';
import withDrawingContainer, { withDrawingContainerProps } from './withDrawingContainer';
import { Circle, Label, Line, Tag, Text } from 'react-konva';
import { DrawingType, RulerUpdateDetails, rulerEndPointsRadius } from '../constants';
import { getRgbColorString } from '../utils';
import { useIsCreatingNewDrawing } from 'redux/hooks/well';

interface Props extends withDrawingContainerProps {
  xEnd: number;
  yEnd: number;
  color: string;
  rulerSizeText: string;
  allowResize: boolean;
}
const RulerDrawing = (props: Props) => {
  const {
    id,
    x,
    y,
    xEnd,
    yEnd,
    color,
    rulerSizeText,
    isSelected,
    tpObject,
    updateDrawing,
    saveAndUpdateDrawing,
    allowResize,
  } = props;
  const points = [rulerEndPointsRadius, 0, xEnd - x - rulerEndPointsRadius, yEnd - y];
  const [hover, setHover] = useState<boolean>(false);
  const { isCreatingNewDrawing } = useIsCreatingNewDrawing();
  const padding = 5;

  const rulerResizeStart = (e, isStartingPoint) => {
    e.cancelBubble = true;
    const newX = x + e.target.x();
    const newY = y + e.target.y();
    const [transformedX, transformedY] = tpObject.FromCanvasToDb(newX, newY);
    updateDrawing(
      id,
      DrawingType.Ruler,
      isStartingPoint
        ? { x1: newX, y1: newY, dbX1: transformedX, dbY1: transformedY }
        : { x2: newX, y2: newY, dbX2: transformedX, dbY2: transformedY },
    );
  };

  const rulerResizeEnd = (e) => {
    e.cancelBubble = true;
    saveAndUpdateDrawing(id, DrawingType.Ruler, {
      x1: x,
      y1: y,
      x2: xEnd,
      y2: yEnd,
    });
  };

  return (
    <>
      <Line
        id={`ruler_line_${id}`}
        points={points}
        stroke={getRgbColorString(color)}
        strokeWidth={3}
        shadowBlur={isSelected ? 10 : 0}
        shadowColor={isSelected ? 'black' : ''}
        shadowOpacity={isSelected ? 5 : 0}
        shadowEnabled={isSelected ? true : false}
      />
      <Circle
        id={`rulerdrawing-start-circle-${id}`}
        draggable={allowResize}
        onDragMove={(e) => rulerResizeStart(e, true)}
        onDragEnd={rulerResizeEnd}
        fill={`rgb(${color})`}
        x={rulerEndPointsRadius}
        y={0}
        radius={rulerEndPointsRadius}
      />
      <Circle
        id={`rulerdrawing-end-circle-${id}`}
        draggable={allowResize}
        onDragMove={(e) => rulerResizeStart(e, false)}
        onDragEnd={rulerResizeEnd}
        fill={getRgbColorString(color)}
        x={points[2]}
        y={points[3]}
        radius={rulerEndPointsRadius}
      />
      <Label
        id={`rulerdrawing-label-${id}`}
        x={points[2]}
        y={points[3] + padding}
        opacity={hover || isSelected || isCreatingNewDrawing ? 1 : 0.8}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <Tag
          id={`rulerdrawing-tag-${id}`}
          fill="#FFF8B3"
          cornerRadius={3}
          stroke={hover || isSelected || isCreatingNewDrawing ? '#000000' : '#999999'}
          strokeWidth={1}
          pointerDirection="up"
          pointerWidth={0}
          lineJoin="round"
        />
        <Text
          id={`rulerdrawing-text-${id}`}
          text={rulerSizeText}
          fontSize={12}
          padding={padding}
          fill="black"
        />
      </Label>
    </>
  );
};

const moveActionPreProcessor = (
  props: Props,
  x: number,
  y: number,
): RulerUpdateDetails => {
  const x2 = x + (props.xEnd - props.x);
  const y2 = y + (props.yEnd - props.y);

  const [transformedX1, transformedY1] = props.tpObject.FromCanvasToDb(x, y);
  const [transformedX2, transformedY2] = props.tpObject.FromCanvasToDb(
    x + (props.xEnd - props.x),
    y + (props.yEnd - props.y),
  );

  return {
    x1: x,
    y1: y,
    x2,
    y2,
    dbX1: transformedX1,
    dbY1: transformedY1,
    dbX2: transformedX2,
    dbY2: transformedY2,
  };
};

export default withDrawingContainer(
  RulerDrawing,
  DrawingType.Ruler,
  moveActionPreProcessor,
);
